import { Image, type ImageStyle } from 'expo-image';
import { Platform } from 'react-native';
import { Text, View, YStack } from 'tamagui';

import { formatDateTime } from '@/src/utils/dateFormatter';
import PSBadge from './ui/PSBadge';

// `filter` isn't in expo-image's ImageStyle (it's an experimental web/native
// style RN's types don't cover), so the two platform shapes are typed
// separately and merged into ImageStyle at the use site. Mirrors EventCard.
type GrayscaleStyle = { filter: string } | { filter: { grayscale: number }[] };

/**
 * SecretMenuCard — the Access-Pass "secret menu" tile (molecule).
 *
 * Same anatomy as `EventCard` (fixed 3:4 poster + info panel below at the same
 * width), with two secret-menu-specific differences:
 *   1. A round white logo badge floats over the poster's top-left.
 *   2. The panel copy and treatment are driven by a single `state` union rather
 *      than EventCard's `soldOut` flag, because a secret menu has three
 *      mutually-exclusive states (below).
 *
 * `state` — the only way the card varies, union'd so illegal combos can't
 * compile (same pattern as EventCard's `PanelColor`):
 *   - `'available'` — we know the next slot; the screen passes its raw ISO
 *     datetime string, which the card formats into relative copy (e.g.
 *     "Today · 6:30 PM", "Fri, 27 Jun · 7:30 PM") via `formatSlotDateTime`.
 *     Panel is `$brand` purple.
 *   - `'dropping-soon'` — no future slots scheduled yet. Disabled look; the card
 *     hardwires the panel caption "Slots dropping soon".
 *   - `'sold-out'` — fully booked. Disabled look with NO panel caption; instead a
 *     neutral "Sold out" `PSBadge` floats over the poster's bottom-left, exactly
 *     like `EventCard`'s sold-out treatment. (We deliberately match EventCard
 *     here rather than showing a "Fully booked" line — a sold-out card reads the
 *     same across the app.) The panel keeps only the menu name.
 *
 * The card takes the slot's RAW ISO datetime (not a pre-formatted string) and
 * formats internally — the house rule for dates, so every surface reads the same
 * and the relative-day logic lives in one place. The `dropping-soon` caption is
 * hardwired here, not passed in: it's fixed design copy, so making the screen
 * repeat it would be duplication. The screen still owns the *why* (it decides
 * which state a menu is in); the card owns the *look* and the date formatting per
 * state.
 *
 * Secret-menu panels are ALWAYS `$brand` — except the two disabled states, which
 * go neutral+dimmed (a disabled card is never a highlight). This is why there's
 * no `panelColor` prop like EventCard has: the fill is fully determined by
 * `state`, so exposing it would be a decision already made (dead weight — see
 * the guide's "constrain props / no speculative variants").
 *
 * `width` sizes the whole card (default 240 for the horizontal carousels; a grid
 * passes its measured column width). Height follows the 3:4 portrait ratio.
 * Width/height read Tamagui's unpopulated `size` group and are layout, not design
 * tokens, so raw numbers are correct and unlinted here (same as EventCard).
 *
 * Not pressable: unlike EventCard these don't open on tap, so there's no
 * `onPress`. Add one only when a call site needs it.
 */
const DEFAULT_WIDTH = 240;
// Portrait 3:4 (width:height) → height is 4/3 of the width. Matches EventCard.
const RATIO = 4 / 3;
// The floating logo badge. A fixed layout size (not a design token) — it reads
// the unpopulated `size` group, same as the card width.
const LOGO_SIZE = 48;

// Desaturate the poster for the disabled states. RN/CSS `filter` style, not a
// token: exact on web (where the design is reviewed), riding the experimental
// native filter otherwise and degrading gracefully. Copied from EventCard.
const grayscale: GrayscaleStyle =
  Platform.OS === 'web'
    ? { filter: 'grayscale(1)' }
    : { filter: [{ grayscale: 1 }] };

export type SecretMenuData = {
  imageUrl: string;
  logoUrl: string;
  name: string;
  // The next slot's raw ISO datetime (e.g. "2025-05-06T11:30:40"). Formatted
  // internally and shown only when state is 'available'; the two disabled states
  // have no next slot and use their own hardwired captions, so it's optional and
  // ignored there.
  slotDateTime?: string;
};

// The three mutually-exclusive states a secret menu can be in. Union'd so TS
// rejects anything else.
export type SecretMenuState = 'available' | 'dropping-soon' | 'sold-out';

// Hardwired panel caption for the dropping-soon state (fixed design copy).
// 'available' formats its slot instead; 'sold-out' shows a badge, no caption.
const DROPPING_SOON_CAPTION = 'Slots dropping soon';

export default function SecretMenuCard({
  menu,
  state,
  width = DEFAULT_WIDTH,
}: {
  menu: SecretMenuData;
  state: SecretMenuState;
  width?: number;
}) {
  const available = state === 'available';
  const soldOut = state === 'sold-out';
  // Available → brand purple panel; the two disabled states → neutral + dimmed.
  const panelBg = available ? '$panelTranslucent' : '$backgroundSecondary';
  const titleColor = available ? '$textWhite' : '$textDark';
  const metaColor = available ? '$textWhiteMuted' : '$textDarkMuted';
  // The panel caption per state: available formats its next-slot datetime,
  // dropping-soon shows fixed copy, sold-out shows nothing (its status lives in
  // the image badge instead, matching EventCard). `slotDateTime` is required in
  // practice when available (see the type note); guard so a missing value
  // renders empty rather than crashing the formatter.
  const caption = available
    ? menu.slotDateTime
      ? 'Next slot - ' + formatDateTime(menu.slotDateTime)
      : ''
    : state === 'dropping-soon'
      ? DROPPING_SOON_CAPTION
      : '';
  const imgH = width * RATIO;

  return (
    <YStack
      width={width}
      // Fill the row's height when a parent row stretches us. alignSelf — NOT
      // flex — because flex is the main (horizontal) axis and would fight the
      // fixed width and break wrap-grid layout. See EventCard's note.
      alignSelf="stretch"
    >
      <View width={width} height={imgH} rounded="$base" overflow="hidden">
        <Image
          source={{ uri: menu.imageUrl }}
          style={[
            { width, height: imgH, opacity: available ? 1 : 0.6 },
            available ? null : (grayscale as ImageStyle),
          ]}
          contentFit="cover"
          transition={200}
        />
        {/* Round white logo badge, floated over the poster's top-left. Lives
          inside the overflow-hidden box so it clips to the rounded corners;
          absolutely positioned since its only job is to float over the image. */}
        <View
          position="absolute"
          top="$md"
          left="$md"
          width={LOGO_SIZE}
          height={LOGO_SIZE}
          rounded="$full"
          overflow="hidden"
          backgroundColor="$textWhite"
        >
          <Image
            source={{ uri: menu.logoUrl }}
            style={{ width: LOGO_SIZE, height: LOGO_SIZE }}
            contentFit="cover"
            transition={200}
          />
        </View>
        {soldOut ? (
          // Float the "Sold out" badge over the poster's bottom-left, same as
          // EventCard, so a sold-out secret menu reads identically across the
          // app. The parent's overflow="hidden" clips it to the rounded corners.
          <View position="absolute" bottom="$sm" left="$sm">
            <PSBadge label="Sold out" variant="neutral" />
          </View>
        ) : null}
      </View>

      <YStack
        // Grow to fill the card's height when a row stretches it taller than the
        // text, so the panel extends to the bottom. The poster is fixed-height,
        // so only this grows. Same as EventCard.
        flex={1}
        marginTop="$sm"
        backgroundColor={panelBg}
        opacity={available ? 1 : 0.6}
        rounded="$base"
        p="$md"
        gap="$xs"
      >
        <Text
          fontFamily="$body"
          fontSize="$base"
          fontWeight="$semibold"
          numberOfLines={2}
          color={titleColor}
        >
          {menu.name}
        </Text>
        {caption ? (
          <Text
            fontFamily="$body"
            fontSize="$base"
            numberOfLines={2}
            color={metaColor}
          >
            {caption}
          </Text>
        ) : null}
      </YStack>
    </YStack>
  );
}
